import { useEffect, useId, useRef, useState } from 'react';
import type { Html5Qrcode } from 'html5-qrcode';
import { Button } from '@/components/ui';
import { Camera, CameraOff } from 'lucide-react';

// B-1: 카메라 QR 스캐너. html5-qrcode 를 동적 import (코드 스플릿 — 무거운 디코더를
// 스캔 시작 시점에만 로드). 스캔된 토큰을 onScan 으로 상위(예약 입장 처리)로 전달.

const SCAN_REGION = 240;

export interface QrCameraScannerProps {
  onScan: (token: string) => void;
  disabled?: boolean;
}

export const QrCameraScanner = ({ onScan, disabled }: QrCameraScannerProps) => {
  const regionId = useId().replace(/:/g, '');
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const lastTokenRef = useRef<string>('');
  const [active, setActive] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);

  const stop = async () => {
    const scanner = scannerRef.current;
    scannerRef.current = null;
    setActive(false);
    if (!scanner) return;
    try {
      await scanner.stop();
      scanner.clear();
    } catch {
      // 이미 정지된 경우 무시.
    }
  };

  const start = async () => {
    setError(null);
    setStarting(true);
    try {
      const { Html5Qrcode } = await import('html5-qrcode');
      const scanner = new Html5Qrcode(regionId);
      scannerRef.current = scanner;
      await scanner.start(
        { facingMode: 'environment' },
        { fps: 10, qrbox: { width: SCAN_REGION, height: SCAN_REGION } },
        (decodedText) => {
          // 동일 토큰 연속 디코드 디바운스.
          if (decodedText && decodedText !== lastTokenRef.current) {
            lastTokenRef.current = decodedText;
            onScan(decodedText.trim());
          }
        },
        undefined,
      );
      setActive(true);
    } catch {
      // 카메라 권한 거부 / 장치 없음 / 비보안(HTTP) 컨텍스트 등.
      setError('카메라를 시작할 수 없습니다. 브라우저 카메라 권한과 HTTPS 환경을 확인해 주세요.');
      await stop();
    } finally {
      setStarting(false);
    }
  };

  useEffect(() => () => { void stop(); }, []);

  return (
    <div className="flex flex-col gap-2">
      <div
        id={regionId}
        className="mx-auto overflow-hidden rounded-md"
        style={{
          width: active ? SCAN_REGION : 0,
          height: active ? SCAN_REGION : 0,
          background: 'var(--surface-1)',
          border: active ? '1px solid var(--divider-strong)' : 'none',
        }}
      />
      <div className="flex items-center gap-2">
        {active ? (
          <Button variant="outline" size="sm" onClick={() => void stop()}>
            <CameraOff size={15} aria-hidden="true" />
            스캐너 중지
          </Button>
        ) : (
          <Button size="sm" onClick={() => void start()} disabled={disabled || starting}>
            <Camera size={15} aria-hidden="true" />
            {starting ? '카메라 시작 중...' : '카메라로 QR 스캔'}
          </Button>
        )}
      </div>
      {error ? (
        <p role="alert" className="text-sm" style={{ color: 'var(--status-danger-fg)' }}>
          {error}
        </p>
      ) : null}
    </div>
  );
};
